import type { Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents } from '../../shared/types';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type Turn = { role: 'user' | 'assistant'; content: string };

let panelEl: HTMLDivElement | null = null;
let history: Turn[] = [];
// Captured right before every request so the matching rules_answer knows what
// question/context it was for — needed to render the "escalate" button and,
// separately, to avoid pushing escalated (full-review) answers into `history`.
let pendingContext: { question: string; historyBefore: Turn[] } | null = null;

/** Escape HTML entities, then convert markdown [text](url) links to <a> tags. */
function renderText(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener" ' +
    'style="color:#7ab8f5;text-decoration:underline;cursor:pointer;">$1</a>',
  );
}

function appendMessage(
  role: 'user' | 'assistant' | 'error',
  text: string,
  opts?: { escalate?: { question: string; historyBefore: Turn[] }; socket?: GameSocket; fullReview?: boolean },
): void {
  const box = document.getElementById('rules-messages');
  if (!box) return;

  const wrap = document.createElement('div');

  const el = document.createElement('div');
  el.style.cssText = [
    'margin:6px 0', 'padding:8px 12px', 'border-radius:6px',
    'font-size:13px', 'line-height:1.5', 'white-space:pre-wrap', 'word-break:break-word',
    role === 'user'  ? 'background:#1a3a1a;color:#cce8cc;text-align:right;margin-left:20%' :
    role === 'error' ? 'background:#3a1a1a;color:#e88;font-style:italic' :
    opts?.fullReview  ? 'background:#1e2a3a;color:#ddd;margin-right:20%;border:1px solid #2a4a6a' :
                       'background:#1e2a1e;color:#ddd;margin-right:20%',
  ].join(';');
  const prefix = opts?.fullReview ? '<div style="color:#7ab8f5;font-size:11px;font-weight:bold;margin-bottom:4px;">🔍 Full document review</div>' : '';
  el.innerHTML = prefix + renderText(text);
  wrap.appendChild(el);

  if (opts?.escalate && opts.socket) {
    const escalate = opts.escalate;
    const socket = opts.socket;
    const btn = document.createElement('button');
    btn.textContent = '🔍 Not satisfied? Get a full-document review';
    btn.className = 'rules-escalate-btn';
    btn.style.cssText = 'margin:0 0 10px;padding:4px 10px;font-size:11px;background:#243824;' +
      'color:#88aa88;border:1px solid #3a6a3a;border-radius:4px;cursor:pointer;';
    btn.addEventListener('click', () => {
      btn.disabled = true;
      btn.dataset.used = 'true'; // stays disabled even after setLoading(false) re-enables the others
      btn.textContent = 'Escalating…';
      setLoading(true);
      pendingContext = null; // the escalated answer gets no escalate button and isn't added to history
      socket.emit('rules_escalate', { question: escalate.question, history: escalate.historyBefore });
    });
    wrap.appendChild(btn);
  }

  // Remove placeholder if present
  const ph = box.querySelector('[data-placeholder]');
  if (ph) ph.remove();
  box.appendChild(wrap);
  box.scrollTop = box.scrollHeight;
}

function setLoading(on: boolean): void {
  const input   = document.getElementById('rules-input')  as HTMLInputElement | null;
  const sendBtn = document.getElementById('rules-send')   as HTMLButtonElement | null;
  if (input)   input.disabled   = on;
  if (sendBtn) { sendBtn.disabled = on; sendBtn.textContent = on ? '…' : 'Send'; }
  // Also gate older escalate buttons — otherwise clicking one while a newer
  // question is still in flight clobbers pendingContext and the newer answer
  // silently loses its own escalate button.
  document.querySelectorAll<HTMLButtonElement>('.rules-escalate-btn').forEach(btn => {
    if (btn.dataset.used === 'true') return; // already escalated — stays disabled for good
    btn.disabled = on;
  });
}

function resetMessages(): void {
  const box = document.getElementById('rules-messages');
  if (!box) return;
  box.innerHTML = `<div data-placeholder style="color:#666;font-size:13px;font-style:italic;
    text-align:center;margin-top:40px;">Ask a rules question about Memoir '44.<br>
    Follow-up questions are supported.</div>`;
}

export function openRulesPanel(socket: GameSocket): void {
  if (panelEl) { panelEl.style.display = 'flex'; return; }

  const panel = document.createElement('div');
  panel.id = 'rules-panel';
  panel.style.cssText = [
    'position:fixed', 'top:50%', 'left:50%', 'transform:translate(-50%,-50%)',
    'width:520px', 'max-width:92vw', 'height:520px', 'max-height:88vh',
    'background:#111e11', 'border:1px solid #3a6a3a', 'border-radius:10px',
    'box-shadow:0 8px 32px rgba(0,0,0,0.7)', 'display:flex', 'flex-direction:column',
    'z-index:99999', 'font-family:Arial,sans-serif',
  ].join(';');

  panel.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;
                padding:10px 14px;border-bottom:1px solid #2a4a2a;flex-shrink:0;">
      <span style="color:#d4af37;font-weight:bold;font-size:15px;">📖 Rules Arbiter</span>
      <div style="display:flex;gap:8px;align-items:center;">
        <button id="rules-clear" style="padding:4px 10px;font-size:12px;background:#2a4a2a;
          color:#aaa;border:1px solid #3a6a3a;border-radius:4px;cursor:pointer;">New Chat</button>
        <button id="rules-close" style="padding:4px 10px;font-size:12px;background:#3a1a1a;
          color:#e88;border:1px solid #6a2a2a;border-radius:4px;cursor:pointer;">✕ Close</button>
      </div>
    </div>
    <div id="rules-messages" style="flex:1;overflow-y:auto;padding:10px 14px;
         scrollbar-width:thin;scrollbar-color:#3a5a3a #111e11;">
      <div data-placeholder style="color:#666;font-size:13px;font-style:italic;
           text-align:center;margin-top:40px;">Ask a rules question about Memoir '44.<br>
        Follow-up questions are supported.</div>
    </div>
    <div style="padding:10px 14px;border-top:1px solid #2a4a2a;display:flex;gap:8px;flex-shrink:0;">
      <input id="rules-input" type="text"
        placeholder="e.g. Can infantry in a town battle across a river?"
        style="flex:1;padding:8px 12px;font-size:13px;border-radius:5px;
               border:1px solid #3a6a3a;background:#152a15;color:#eee;outline:none;" />
      <button id="rules-send"
        style="padding:8px 16px;font-size:13px;background:#2a5a2a;color:#cce8cc;
               border:1px solid #3a8a3a;border-radius:5px;cursor:pointer;white-space:nowrap;">
        Send
      </button>
    </div>`;

  document.body.appendChild(panel);
  panelEl = panel;

  panel.querySelector('#rules-close')!.addEventListener('click', () => {
    if (panelEl) panelEl.style.display = 'none';
  });

  panel.querySelector('#rules-clear')!.addEventListener('click', () => {
    history = [];
    pendingContext = null;
    resetMessages();
  });

  const send = () => {
    const input = document.getElementById('rules-input') as HTMLInputElement | null;
    if (!input) return;
    const question = input.value.trim();
    if (!question) return;
    input.value = '';
    appendMessage('user', question);
    const prevHistory = [...history];
    history.push({ role: 'user', content: question });
    setLoading(true);
    pendingContext = { question, historyBefore: prevHistory };
    socket.emit('rules_question', { question, history: prevHistory });
  };

  panel.querySelector('#rules-send')!.addEventListener('click', send);
  (panel.querySelector('#rules-input') as HTMLInputElement)
    .addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    });

  socket.on('rules_answer', ({ answer, escalated }) => {
    setLoading(false);
    if (escalated) {
      appendMessage('assistant', answer, { fullReview: true });
      return;
    }
    history.push({ role: 'assistant', content: answer });
    appendMessage('assistant', answer, pendingContext ? { escalate: pendingContext, socket } : undefined);
  });

  socket.on('rules_error', (msg) => {
    setLoading(false);
    appendMessage('error', `Error: ${msg}`);
  });

  (panel.querySelector('#rules-input') as HTMLInputElement).focus();
}

export function destroyRulesPanel(socket: GameSocket): void {
  socket.off('rules_answer');
  socket.off('rules_error');
  if (panelEl) { panelEl.remove(); panelEl = null; }
  history = [];
  pendingContext = null;
}
